import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Ban } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { checkTagName, parseTagNameLines, tagNameCheckMessage } from './lib/tagNormalize';

interface TagNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** タグ種別名。ダイアログの見出しに出す */
  tagTypeName: string;
  /** 同じタグ種別に既にあるタグ名。こことだけ突き合わせる（種別が違えば同名は正当） */
  existingNames: string[];
  /** 名前の変更なら、変更前の名前。新規追加なら undefined */
  editingName?: string;
  submitting: boolean;
  onSubmit: (names: string[]) => void;
}

/**
 * タグ名の追加・名前の変更ダイアログ。
 *
 * 追加は複数行のテキストエリアで一度にまとめて登録できる（47都道府県のような
 * 件数を1操作で終わらせるため）。名前の変更は1件のみ。
 *
 * 既存と正規化キーが一致する名前は登録できない。似た名前は警告するが登録はできる
 * （「ロング」と「ロンク」のような別物まで潰さないため）。
 */
export function TagNameDialog({
  open,
  onOpenChange,
  tagTypeName,
  existingNames,
  editingName,
  submitting,
  onSubmit,
}: TagNameDialogProps) {
  const isEditing = editingName !== undefined;
  const [text, setText] = useState('');

  // 名前の変更では、変更前の名前自身との重複判定を外す（同じ名前のまま保存できるように）
  const comparisonNames = useMemo(
    () => (isEditing ? existingNames.filter((n) => n !== editingName) : existingNames),
    [existingNames, editingName, isEditing],
  );

  const names = useMemo(
    () => (isEditing ? [text.trim()].filter(Boolean) : parseTagNameLines(text)),
    [text, isEditing],
  );

  const checks = useMemo(
    () => names.map((name) => ({ name, check: checkTagName(name, comparisonNames) })),
    [names, comparisonNames],
  );

  const blocked = checks.filter(
    ({ check }) => check.kind === 'duplicate' || check.kind === 'too_long',
  );
  const warnings = checks.filter(
    ({ check }) => check.kind === 'near_duplicate' || check.kind === 'similar',
  );
  const submittable = names.length > 0 && blocked.length === 0;

  // 親が open を true にして開くため、開いたタイミングで入力欄を作り直す。
  // 名前の変更では変更前の名前を入れておき、そのまま保存できるようにする
  useEffect(() => {
    if (open) setText(editingName ?? '');
  }, [open, editingName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'タグ名を変更' : 'タグ名を追加'}</DialogTitle>
          <DialogDescription>
            種別: {tagTypeName}
            {isEditing ? '（名前を変えても、この名前が付いている店舗はそのまま残ります）' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tag-name-input">{isEditing ? 'タグ名' : 'タグ名（1行に1つ）'}</Label>
            {isEditing ? (
              <Input
                id="tag-name-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoFocus
              />
            ) : (
              <>
                <Textarea
                  id="tag-name-input"
                  rows={5}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={'東京都\n大阪府\n神奈川県'}
                  autoFocus
                />
                <span className="text-xs text-gray-500">
                  まとめて貼り付けられます。表記が違うだけの同じ名前は1件に畳まれます。
                </span>
              </>
            )}
          </div>

          {blocked.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {blocked.map(({ name, check }) => (
                <p
                  key={`blocked-${name}`}
                  className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                >
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{tagNameCheckMessage(check, name)}</span>
                </p>
              ))}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {warnings.map(({ name, check }) => (
                <p
                  key={`warn-${name}`}
                  className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{tagNameCheckMessage(check, name)}</span>
                </p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            disabled={!submittable || submitting}
            onClick={() => onSubmit(names)}
          >
            {isEditing ? '変更' : names.length > 0 ? `${names.length}件を追加` : '追加'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
